import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { KeyRound, X } from 'lucide-react';
import adminApi from '../api/adminApi';
import PasswordInput from './PasswordInput';
import PasswordRequirements from './PasswordRequirements';
import { isPasswordValid, passwordError, PASSWORDS_DO_NOT_MATCH } from '../utils/passwordPolicy';

// User Management -> Change Password for another user. No current password
// is needed; the backend checks the caller is an admin and signs the target
// user out everywhere.
const AdminChangePasswordModal = ({ user, onClose }) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [touched, setTouched] = useState({});
  const [serverErrors, setServerErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !saving) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, saving]);

  const newError = serverErrors.new_password || (touched.new_password ? passwordError(newPassword) : '');
  const confirmError = serverErrors.confirm_password
    || (touched.confirm_password && !confirmPassword ? 'Please confirm the new password.' : '')
    || (touched.confirm_password && confirmPassword && confirmPassword !== newPassword ? PASSWORDS_DO_NOT_MATCH : '');
  const canSubmit = isPasswordValid(newPassword) && confirmPassword === newPassword && !saving;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ new_password: true, confirm_password: true });
    if (!canSubmit) return;

    setSaving(true);
    setServerErrors({});
    try {
      const response = await adminApi.changeUserPassword(user.id, newPassword, confirmPassword);
      toast.success(response.data.message || 'Password changed successfully.');
      onClose();
    } catch (error) {
      const data = error.response?.data || {};
      if (data.field) setServerErrors({ [data.field]: data.message });
      toast.error(data.message || 'Unable to change password.');
      setSaving(false);
    }
  };

  return (
    // zIndex above Sidebar's 1000/1001, which would otherwise cover part of the modal.
    <div
      onClick={() => { if (!saving) onClose(); }}
      style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: '1rem' }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="admin-cp-title"
        onClick={(e) => e.stopPropagation()}
        style={{ backgroundColor: 'white', borderRadius: '0.75rem', width: '100%', maxWidth: '480px', maxHeight: 'calc(100vh - 2rem)', overflowY: 'auto', boxShadow: '0 20px 40px -10px rgba(0,0,0,0.25)' }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.5rem', borderBottom: '1px solid #e5e7eb' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            <div style={{ width: '32px', height: '32px', borderRadius: '50%', backgroundColor: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <KeyRound size={18} color="#1d4ed8" />
            </div>
            <h3 id="admin-cp-title" style={{ fontSize: '1.05rem', fontWeight: 600, color: '#1f2937', margin: 0 }}>Change Password</h3>
          </div>
          <button onClick={onClose} disabled={saving} aria-label="Close" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', display: 'flex' }}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} noValidate>
          <div style={{ padding: '1.25rem 1.5rem' }}>
            <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '0.35rem 1rem', fontSize: '0.875rem', margin: '0 0 1.25rem', padding: '0.75rem 1rem', backgroundColor: '#f8fafc', borderRadius: '0.5rem' }}>
              <dt style={{ color: '#6b7280' }}>User</dt>
              <dd style={{ margin: 0, color: '#1f2937', fontWeight: 500, wordBreak: 'break-word' }}>{user.username}</dd>
              <dt style={{ color: '#6b7280' }}>Email</dt>
              <dd style={{ margin: 0, color: '#1f2937', wordBreak: 'break-word' }}>{user.email}</dd>
            </dl>

            <PasswordInput
              id="admin-cp-new"
              label="New Password"
              value={newPassword}
              autoFocus
              onChange={(e) => { setNewPassword(e.target.value); setServerErrors({}); }}
              onBlur={() => setTouched((t) => ({ ...t, new_password: true }))}
              error={newError}
              placeholder="Enter new password"
            />
            <PasswordRequirements password={newPassword} />
            <PasswordInput
              id="admin-cp-confirm"
              label="Confirm Password"
              value={confirmPassword}
              onChange={(e) => { setConfirmPassword(e.target.value); setServerErrors({}); setTouched((t) => ({ ...t, confirm_password: true })); }}
              onBlur={() => setTouched((t) => ({ ...t, confirm_password: true }))}
              error={confirmError}
              placeholder="Re-enter new password"
            />
            <p style={{ fontSize: '0.8rem', color: '#6b7280', margin: 0 }}>
              {user.username} will be signed out of all sessions and must sign in with the new password.
            </p>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', padding: '1rem 1.5rem', borderTop: '1px solid #e5e7eb' }}>
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              style={{ padding: '0.625rem 1.25rem', backgroundColor: 'white', color: '#374151', border: '1px solid #d1d5db', borderRadius: '0.5rem', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 500 }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              style={{ padding: '0.625rem 1.5rem', backgroundColor: canSubmit ? '#3b82f6' : '#93c5fd', color: 'white', border: 'none', borderRadius: '0.5rem', cursor: canSubmit ? 'pointer' : 'not-allowed', fontSize: '0.875rem', fontWeight: 500 }}
            >
              {saving ? 'Changing...' : 'Change Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminChangePasswordModal;
